import { DeleteOutlined, MenuOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Flex, Table, TableProps, Typography } from "antd";
import { showConfirmModal } from "components/modals/confirmModal";
import PrimaryAction from "pages/schedule/component/PrimaryAction";
import {
  useRemoveFromWaitlistV2,
  useReorderWaitlistV2,
} from "hooks/mutations/waitlist-v2";
import { bookingsByScheduleV2Options } from "hooks/queries/booking-v2";
import { waitlistByScheduleV2Options } from "hooks/queries/waitlist-v2";
import { WaitlistEntryDto } from "hooks/api/waitlist-v2";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { isWaitlistClosed, waitlistCutoffMinutesOf } from "shared/utils/cutoff";
import { useEffect, useMemo, useRef, useState } from "react";
import BookingDetailDrawer, {
  BookingDetailDrawerBooking,
} from "components/bookings/BookingDetailDrawer.component";
import { tnum } from "shared/components/TableText";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { Schedule } from "shared/types";
import { BookingStatus, ClassCategory } from "shared/types/enum";
import {
  BOOKING_STATUS_LABELS,
  BOOKING_STATUS_TONE,
  hasScheduleStarted,
  isSeatHoldingBookingStatus,
} from "shared/utils";
import {
  DEFAULT_DATE_FORMAT,
  formatDate,
  now,
  tzDate,
} from "shared/utils/dayjs";
import { formatPhone } from "shared/utils/phone";
import { StatusBadge } from "./component/ScheduleStatusBadge";
import { textPx } from "shared/constants/zoom";

// A waitlist row in the shape the booking detail drawer reads. The entry is
// a booking_v2 row like any other, so the same drawer shows its code,
// client, package and activity timeline.
const toDrawerBooking = (
  entry: WaitlistEntryDto,
): BookingDetailDrawerBooking => ({
  id: entry.id,
  customer_id: entry.customer_id,
  customer_package_id: entry.customer_package_id,
  customer_package_name: entry.customer_package_name,
  customer_name: entry.customer_name,
  customer_phone: entry.customer_phone,
  booking_ref: entry.booking_ref ?? null,
  booking_status: entry.status,
  credit_cost: entry.credit_cost,
});

const ScheduleWaitlist = ({
  schedule,
  onWaitlistAdded,
  resumeEntryId,
  onResumeEntryConsumed,
}: {
  schedule: Schedule;
  onWaitlistAdded?: () => void;
  // Booking-ref search deep link to a waitlist entry: open its drawer once
  // the queue has loaded (the ScheduleBooking resumeBookingId counterpart).
  resumeEntryId?: string;
  onResumeEntryConsumed?: () => void;
}) => {
  const scheduleId = schedule.schedule_id;
  const occurrenceTimestamp = schedule.occurrence_timestamp;
  const classType = schedule.class_type.type;
  const isPrivate = classType === ClassCategory.Private;
  const bookingClosed = now().isAfter(tzDate(schedule.end_time).add(1, "hour"));
  const hasStarted = hasScheduleStarted(schedule);

  // Waitlist closes at the configured auto-promote cutoff (hours or minutes
  // before start, shared/utils/cutoff.ts), not only at class start. Computed
  // from time so the closed state shows immediately, even before the server's
  // lazy cleanup expires the rows.
  const { data: settings } = useBusinessSettings();
  const waitlistClosed =
    hasStarted ||
    isWaitlistClosed(occurrenceTimestamp, waitlistCutoffMinutesOf(settings));

  const { data: bookings } = useQuery(
    bookingsByScheduleV2Options(scheduleId, occurrenceTimestamp),
  );
  const { data: entries, isLoading } = useQuery(
    waitlistByScheduleV2Options(scheduleId, occurrenceTimestamp),
  );

  const [detailEntry, setDetailEntry] = useState<WaitlistEntryDto | null>(
    null,
  );

  useEffect(() => {
    if (!resumeEntryId || !entries) return;
    const match = entries.find((e) => e.id === resumeEntryId);
    if (match) setDetailEntry(match);
    onResumeEntryConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeEntryId, entries]);

  const { mutate: removeFromWaitlist } = useRemoveFromWaitlistV2(
    scheduleId,
    occurrenceTimestamp,
  );
  const { mutate: reorderWaitlist } = useReorderWaitlistV2(
    scheduleId,
    occurrenceTimestamp,
  );

  const confirmedCount = (bookings ?? []).filter((b) =>
    isSeatHoldingBookingStatus(b.booking_status),
  ).length;
  // Per-schedule snapshot first, else the live class max (see ScheduleBooking).
  // Keeps the "class full → waitlist" gate aligned with the server's
  // effectiveCapacity so a later class-max edit doesn't shift existing schedules.
  const capacity = schedule.capacity ?? schedule.class_type.max_participants;

  // Snapshot rows (expired) are non-interactive history. Drag/Hapus and the
  // cap check only consider live rows.
  const liveEntries = useMemo(
    () => (entries ?? []).filter((e) => e.status === BookingStatus.Waitlist),
    [entries],
  );
  const expiredEntries = useMemo(
    () =>
      (entries ?? []).filter(
        (e) => e.status === BookingStatus.WaitlistExpired,
      ),
    [entries],
  );
  const liveWaitlistLength = liveEntries.length;
  const hasExpiredRows = expiredEntries.length > 0;
  const tableData = entries ?? [];

  const draggedIdxRef = useRef<number | null>(null);
  const isExpiredRow = (row: WaitlistEntryDto) =>
    row.status === BookingStatus.WaitlistExpired;

  const openReorderConfirm = (
    row: WaitlistEntryDto,
    fromIdx: number,
    toIdx: number,
  ) => {
    showConfirmModal({
      title: translate("schedule.waitlist_reorder.title"),
      confirmLabel: translate("schedule.waitlist_reorder.confirm"),
      content: (
        <Flex vertical gap={4} style={{ marginTop: 8 }}>
          <Typography.Text>
            <Typography.Text strong>{row.customer_name}</Typography.Text>{" "}
            {translate("schedule.waitlist_reorder.body_1")}{" "}
            <Typography.Text strong>#{fromIdx + 1}</Typography.Text>{" "}
            {translate("schedule.waitlist_reorder.body_2")}{" "}
            <Typography.Text strong>#{toIdx + 1}</Typography.Text>.
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
            {translate("schedule.waitlist_reorder.note")}
          </Typography.Text>
        </Flex>
      ),
      onConfirm: () =>
        reorderWaitlist({
          bookingId: row.id,
          newPosition: toIdx + 1,
        }),
    });
  };

  const DraggableRow = (
    props: React.HTMLAttributes<HTMLTableRowElement> & {
      "data-row-index"?: number;
    },
  ) => {
    const idx = props["data-row-index"];
    const row = idx != null ? tableData[idx] : undefined;
    const expired = row ? isExpiredRow(row) : false;
    const canDrag = !bookingClosed && !hasStarted && !expired;
    // A client deactivated at this studio can't be moved (the server refuses
    // too); the row still takes a drop, which moves someone else.
    const canPickUp = canDrag && row?.customer_active !== false;
    return (
      <tr
        {...props}
        draggable={canPickUp}
        onDragStart={(e) => {
          if (idx == null || !canPickUp) return;
          draggedIdxRef.current = idx;
          e.currentTarget.classList.add("dragging");
        }}
        onDragEnd={(e) => {
          e.currentTarget.classList.remove("dragging");
          document
            .querySelectorAll("tr.drag-over")
            .forEach((r) => r.classList.remove("drag-over"));
        }}
        onDragOver={(e) => {
          if (!canDrag) return;
          e.preventDefault();
          e.currentTarget.classList.add("drag-over");
        }}
        onDragLeave={(e) => {
          e.currentTarget.classList.remove("drag-over");
        }}
        onDrop={(e) => {
          e.preventDefault();
          const from = draggedIdxRef.current;
          const to = idx;
          draggedIdxRef.current = null;
          if (from == null || to == null || from === to) return;
          const targetRow = tableData[to];
          if (!targetRow || isExpiredRow(targetRow)) return;
          const sourceRow = tableData[from];
          if (!sourceRow || isExpiredRow(sourceRow)) return;
          // Defer modal so dragend cleanup runs first
          setTimeout(() => openReorderConfirm(sourceRow, from, to), 0);
        }}
      />
    );
  };

  const confirmRemove = (entry: WaitlistEntryDto) => {
    showConfirmModal({
      tone: "error",
      title: translate("messages.warning.remove_from_waitlist.title"),
      content: translate(
        "messages.warning.remove_from_waitlist.content",
      ).replace("{{name}}", entry.customer_name),
      confirmLabel: translate("button.delete"),
      destructive: true,
      onConfirm: () => removeFromWaitlist(entry.id),
    });
  };

  const columns: TableProps<WaitlistEntryDto>["columns"] = [
    {
      title: "",
      key: "drag",
      width: 40,
      render: (_, entry) =>
        isExpiredRow(entry) ? null : entry.customer_active === false ? (
          <MenuOutlined style={{ cursor: "not-allowed", color: colors.ink4 }} />
        ) : (
          <MenuOutlined style={{ cursor: "grab", color: colors.ink3 }} />
        ),
    },
    {
      title: "#",
      dataIndex: "waitlist_position",
      key: "waitlist_position",
      width: 60,
    },
    {
      title: translate("label.customer_name"),
      dataIndex: "customer_name",
      key: "customer_name",
    },
    {
      title: translate("label.phone_number"),
      dataIndex: "customer_phone",
      key: "customer_phone",
      render: (_, { customer_phone }) => (
        <span style={tnum}>{formatPhone(customer_phone)}</span>
      ),
    },
    {
      title: translate("package"),
      dataIndex: "customer_package_name",
      key: "customer_package_name",
    },
    {
      // Credits the spot holds; a membership spot holds none.
      title: translate("label.credits"),
      dataIndex: "credit_cost",
      key: "credit_cost",
      width: 90,
      render: (_, { credit_cost, package_unlimited }) => (
        <span style={tnum}>{package_unlimited ? "-" : (credit_cost ?? 1)}</span>
      ),
    },
    {
      title: translate("label.booking_date"),
      dataIndex: "joined_at",
      key: "joined_at",
      render: (_, { joined_at }) =>
        formatDate(joined_at, DEFAULT_DATE_FORMAT + ", HH:mm"),
    },
    {
      title: translate("label.status"),
      key: "status",
      render: (_, entry) =>
        isExpiredRow(entry) ? (
          <StatusBadge
            tone={BOOKING_STATUS_TONE[BookingStatus.WaitlistExpired]}
            label={BOOKING_STATUS_LABELS[BookingStatus.WaitlistExpired]}
          />
        ) : (
          <StatusBadge
            tone={BOOKING_STATUS_TONE[BookingStatus.Waitlist]}
            label={BOOKING_STATUS_LABELS[BookingStatus.Waitlist]}
          />
        ),
    },
    {
      title: translate("label.action"),
      key: "action",
      // The remove button must not also open the row's detail drawer.
      render: (_, entry) =>
        isExpiredRow(entry) ? null : (
          <Flex gap={4} onClick={(e) => e.stopPropagation()}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              // Read-only while the client is deactivated at this studio.
              disabled={entry.customer_active === false}
              onClick={() => confirmRemove(entry)}
            />
          </Flex>
        ),
    },
  ];

  return (
    <Card>
      <Flex vertical gap={18}>
        <Flex justify="space-between" align="flex-start" gap={16}>
          <Flex vertical>
            <Typography.Title
              level={5}
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: textPx(17),
                letterSpacing: "-0.01em",
              }}
            >
              {translate("title.schedule_waitlist")}
            </Typography.Title>
            <Typography.Text
              style={{ color: colors.ink2, fontSize: textPx(13), marginTop: 4 }}
            >
              {translate("subtitle.schedule_waitlist")}
            </Typography.Text>
          </Flex>
          <PrimaryAction
            scheduleId={scheduleId}
            occurrenceTimestamp={occurrenceTimestamp}
            classId={schedule.class_type.id}
            isPrivate={isPrivate}
            locked={schedule.is_cancelled}
            hasStarted={hasStarted}
            confirmedCount={confirmedCount}
            capacity={capacity}
            waitlistLength={liveWaitlistLength}
            onWaitlistAdded={onWaitlistAdded}
          />
        </Flex>
        {(waitlistClosed || hasExpiredRows) && (
          <Alert
            type="info"
            showIcon
            message={translate("messages.info.waitlist_closed")}
          />
        )}
        <Table
          className="waitlist-table"
          rowKey={(record) => record.id}
          dataSource={tableData}
          loading={isLoading}
          columns={columns}
          size={"middle"}
          pagination={false}
          components={{ body: { row: DraggableRow } }}
          // A click opens the entry's detail drawer, like a booking row. A
          // drag never fires a click, so reordering is unaffected.
          onRow={(record, idx) =>
            ({
              "data-row-index": idx,
              onClick: () => setDetailEntry(record),
              style: { cursor: "pointer" },
            } as React.HTMLAttributes<HTMLTableRowElement>)
          }
          rowClassName={(record) =>
            isExpiredRow(record) ? "waitlist-row--expired" : ""
          }
        />
      </Flex>
      <BookingDetailDrawer
        booking={detailEntry ? toDrawerBooking(detailEntry) : null}
        onClose={() => setDetailEntry(null)}
      />
    </Card>
  );
};

export default ScheduleWaitlist;
